import { useMemo } from 'react'
import { openEliteLandingPage, postCTA, trackEvent } from '../../../../../utils/appBridge'
import { EventNames, type ElitePlanOfferType } from '../../../../../types/misc.types'
import {
  EliteSpecialOfferGiftBanner,
  resolveSpecialOfferGiftBannerVariant,
  shouldShowEliteOfferBanner
} from '../../../EliteSpecialOfferGiftBanner'
import { useEliteBuyCtaExtras } from '../../../EliteBuyCtaExtrasContext'

type EliteOfferBannerSource = 'BrokeEliteLandingPage' | 'EliteLandingPageV8' | 'RebalancePlan' | 'NoRebalanceNeeded' | 'CountdownView' | 'PostCountDownNonElite' | 'Stopwatch'

interface EliteOfferBannerProps {
  offerStartTime?: number | null
  offerExpiryTime?: number | null
  membershipFees?: number
  offerType?: ElitePlanOfferType | null
  specialOffering?: boolean | null
  signupOffer?: boolean | null
  notElitePage?: boolean
  onClick?: () => void
  cta?: Record<any, any> | null
  source?: EliteOfferBannerSource
  screenType?: string
}

export const EliteOfferBanner = ({
  offerStartTime,
  offerExpiryTime,
  membershipFees,
  offerType,
  specialOffering,
  signupOffer,
  notElitePage,
  onClick,
  cta,
  source,
  screenType
}: EliteOfferBannerProps) => {
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const bannerVariant = useMemo(
    () => resolveSpecialOfferGiftBannerVariant(offerType, membershipFees),
    [offerType, membershipFees]
  )

  const isNotElitePage = useMemo(() => {
    if (notElitePage !== undefined) {
      return notElitePage
    }
    if (source) {
      return source === 'RebalancePlan' ||
        source === 'NoRebalanceNeeded' ||
        source === 'CountdownView' ||
        source === 'PostCountDownNonElite' ||
        source === 'Stopwatch'
    }
    return true
  }, [notElitePage, source])

  const handleClick = () => {
    const eventId = source || 'EliteOfferBanner'
    const detectedScreenType = screenType || (isNotElitePage ? 'MF_Smart_Rebalancing' : 'MF_Elite_Landing')

    trackEvent({
      eventName: EventNames.UserClicked,
      payload: {
        ...eliteBuyCtaExtras,
        eventId: `EliteOfferBanner_${eventId}`,
        buttonLabel: 'Elite Offer Banner',
        screenType: detectedScreenType
      }
    })

    if (onClick) {
      onClick()
      return
    }

    if (!isNotElitePage && cta) {
      postCTA(JSON.stringify(cta))
    } else {
      openEliteLandingPage()
    }
  }
  const isOfferActive = useMemo(
    () =>
      shouldShowEliteOfferBanner({
        offerType,
        specialOffering,
        signupOffer,
        offerStartTime,
        offerExpiryTime
      }),
    [offerType, specialOffering, signupOffer, offerStartTime, offerExpiryTime]
  )

  if (!isOfferActive) return null

  return (
    <div className={`mx-5 mt-8 w-full ${isNotElitePage ? '!mx-0 !mb-4' : ''}`}>
      <div
        className={`bg-orange30 rounded-xl ${isNotElitePage ? '!mx-0' : '!mx-5'} w-auto overflow-hidden`}
      >
        <EliteSpecialOfferGiftBanner
          variant={bannerVariant}
          offerType={offerType}
          signupOffer={signupOffer}
          offerValidTill={offerExpiryTime}
          onClick={handleClick}
        />
      </div>
    </div>
  )
}
